import { calcularIdade, PROGRAMA_VETERANO } from './inscricaoSections';
import { parseDecimal } from '../../../../utils';
import { consolidarOcupacoes, rendasDoMembro, somarRendas } from './rendaMembro';
import { encontrarIndiceMembro } from './membroFamiliar';

/* Estado inicial de uma inscrição nova, antes do prefill da identificação. */
export const estadoInicialInscricao = {
    id: null,
    vaga: null,
    tipoMoradia: null,
    tipoHabitacao: null,
    codigoAluno: null,
    pessoa: {
        nome: '',
        nomeSocial: '',
        emailAtual: '',
        telefone: '',
        celular: '',
        celularDois: '',
        dataNascimento: null,
        sexo: '',
        estadoCivil: '',
        raca: '',
        nacionalidade: '',
        nomeMae: '',
        nomePai: '',
        cpf: '',
        rg: '',
        orgaoRg: '',
        deficiencias: []
    },
    endereco: {
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: {
            nome: '',
            uf: {
                uf: ''
            }
        }
    },
    responsavelLegal: {
        nome: '',
        cpf: '',
        rg: '',
        dataNascimento: null,
        emailAtual: '',
        celular: '',
        tipoResponsavelLegal: '',
        endereco: {
            cep: '',
            logradouro: '',
            numero: '',
            complemento: '',
            bairro: '',
            cidade: {
                nome: '',
                uf: {
                    uf: ''
                }
            }
        }
    },
    tipoResponsavelLegal: '',
    dataEmancipacao: null,
    utilizacaoNota: '',

    // Socioeconômico
    parentesBeneficiarios: [],
    declaracaoIR: 'N',
    cadUnicoOuTransferenciaRenda: false,
    vlrPensao: '',
    vlrPensaoFilho: '',
    disponibilidade: '',
    rendas: [], // Rendas do candidato
    composicaoFamiliar: [],
    enderecoComposicaoFamiliares: [],

    // Dados da Inscrição
    sistemaCotas: false,
    itemSelecionadoPsInscricao: null,
    idProcessoSeletivo: null,
    categoriaSelecionadoPsInscricao: '',
    inscricaoVeterano: {
        possuiEnem: null,
        possuiBeneficioNaIes: null,
        descricaoBeneficio: '',
        participouProcessoAnterior: null,
        descricaoProcessoAnterior: ''
    },
    inscricaoEnsino: [],
    inscricaoEnem: {
        processoSeletivoEnem: null,
        cdEnem: '',
        pontuacao: '',
        nomeArquivo: '',
        arquivo: null,
        mimetype: ''
    },
    motivo: '',

    // Anexos
    inscricaoAnexos: [],
    permitirEntregaDocumentos: false,
    permitirEntregaDocumentoPendentes: false,
    inscricaoPendenciaAnexos: [],
    documentosExigidos: [],
    programaId: null,
    configuracoesFormulario: [],
    inscricoesProcessoSeletivo: [],
    categoriasInscricao: [],

    // Termos
    aceitarTermos: false
};

/* O CEP vem do banco como número: devolve os zeros à esquerda para a máscara (99999-999). */
export const cepComZeros = (cep) => (cep ? String(cep).replace(/\D/g, '').padStart(8, '0') : '');

/* Data em dd/MM/yyyy, ISO ou Date, sempre convertida para dd/MM/yyyy (formato dos campos do formulário). */
export const formatarDataDDMMYYYY = (dateVal) => {
    if (!dateVal) return '';
    if (typeof dateVal === 'string') {
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateVal)) {
            return dateVal;
        }
        const match = dateVal.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            return `${match[3]}/${match[2]}/${match[1]}`;
        }
    }
    try {
        const d = new Date(dateVal);
        if (!isNaN(d.getTime())) {
            const dateStr = typeof dateVal === 'string' ? dateVal : d.toISOString();
            const parts = dateStr.split('T')[0].split('-');
            if (parts.length === 3) {
                return `${parts[2]}/${parts[1]}/${parts[0]}`;
            }
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}/${month}/${year}`;
        }
    } catch (e) {
        // Data em formato inesperado: segue como veio.
    }
    return dateVal;
};

/*
 * Estado inicial da inscrição a partir da identificação: reproduz o prefill que
 * o PageInscricaoNova fazia no efeito, tanto para quem já tem cadastro
 * (dadosCadastrais) quanto para quem está se identificando pela primeira vez.
 */
export const montarEstadoInicial = (identificacao, { isEdit } = {}) => {
    if (identificacao.dadosCadastrais) {
        const dc = identificacao.dadosCadastrais;
        const candidate = dc.pessoa || {};
        const addr = candidate.enderecos?.[0] || {};

        const respRelation = candidate.responsavelAtual || {};
        const resp = respRelation.responsavel || {};
        const respAddr = resp.enderecos?.[0] || {};

        // Datas no formato DD/MM/YYYY
        const candidateDob = formatarDataDDMMYYYY(candidate.dataNascimento || identificacao.dataNascimento);
        const candidateEmanc = formatarDataDDMMYYYY(candidate.dataEmancipacao);
        const respDob = formatarDataDDMMYYYY(resp.dataNascimento);

        // Telefones: combina DDD + número quando o back manda separado
        const formatPhone = (ddd, num) => {
            if (!num) return '';
            if (ddd) {
                const cleanDdd = String(ddd).replace(/\D/g, '');
                const cleanNum = String(num).replace(/\D/g, '');
                return `(${cleanDdd}) ${cleanNum}`;
            }
            return num;
        };

        const tel = formatPhone(addr.dddTelefonePessoa, addr.telefonePessoa) || candidate.telefone || '';
        const cel = formatPhone(addr.dddCelularPessoa, addr.celularPessoa) || candidate.celular || '';
        const cel2 = formatPhone(addr.dddCelularPessoaDois, addr.celularPessoaDois) || candidate.celularDois || '';
        const respCel = formatPhone(respAddr.dddCelularPessoa, respAddr.celularPessoa) || resp.celular || '';

        const getCidadeObject = (cidadeObj) => {
            if (!cidadeObj) {
                return { nome: '', uf: { uf: '' } };
            }
            if (typeof cidadeObj === 'string') {
                return { nome: cidadeObj, uf: { uf: '' } };
            }
            return {
                id: cidadeObj.id || null,
                nome: cidadeObj.nome || '',
                uf: {
                    uf: cidadeObj.uf?.uf || cidadeObj.uf || ''
                }
            };
        };

        /* Menor emancipado: o tipo de responsável vira EMANCIPADO mesmo sem responsável cadastrado. */
        const tipoResponsavelLegal = candidate.emancipada === 'SIM' ? 'EMANCIPADO' : (respRelation.tipoResponsavel || '');

        return {
            id: isEdit ? (dc.id || null) : null,
            vaga: isEdit ? (dc.vaga || null) : null,
            tipoMoradia: dc.tipoMoradia || null,
            tipoHabitacao: dc.tipoHabitacao || null,
            codigoAluno: dc.codigoAluno ?? null,
            pessoa: {
                nome: candidate.nome || identificacao.nome || '',
                nomeSocial: candidate.nomeSocial || '',
                emailAtual: candidate.emailAtual || '',
                telefone: tel,
                celular: cel,
                celularDois: cel2,
                dataNascimento: candidateDob,
                sexo: candidate.sexo || '',
                estadoCivil: candidate.estadoCivil?.id || candidate.estadoCivil || '',
                raca: candidate.raca?.id || candidate.raca || '',
                nacionalidade: candidate.nacionalidade?.id || candidate.nacionalidade || '',
                nomeMae: candidate.nomeMae || identificacao.nomeMae || '',
                nomePai: candidate.nomePai || '',
                cpf: candidate.cpf || identificacao.cpf || '',
                rg: candidate.rg || '',
                orgaoRg: candidate.orgaoRg || '',
                deficiencias: (candidate.deficiencias || []).map(d => d.deficiencia?.id || d.deficiencia || d)
            },
            endereco: {
                cep: cepComZeros(addr.cep),
                logradouro: addr.logradouro || '',
                numero: addr.numero || '',
                complemento: addr.complemento || '',
                bairro: addr.bairro || '',
                cidade: getCidadeObject(addr.cidade)
            },
            responsavelLegal: {
                nome: resp.nome || '',
                cpf: resp.cpf || '',
                rg: resp.rg || '',
                dataNascimento: respDob,
                emailAtual: resp.emailAtual || '',
                celular: respCel,
                tipoResponsavelLegal,
                endereco: {
                    cep: cepComZeros(respAddr.cep),
                    logradouro: respAddr.logradouro || '',
                    numero: respAddr.numero || '',
                    complemento: respAddr.complemento || '',
                    bairro: respAddr.bairro || '',
                    cidade: getCidadeObject(respAddr.cidade)
                }
            },
            tipoResponsavelLegal,
            dataEmancipacao: candidateEmanc,
            parentesBeneficiarios: dc.parentesBeneficiarios || [],
            declaracaoIR: dc.declaracaoIR || 'N',
            cadUnicoOuTransferenciaRenda: dc.cadUnicoOuTransferenciaRenda || false,
            vlrPensao: dc.vlrPensao ?? '',
            vlrPensaoFilho: dc.vlrPensaoFilho ?? '',
            disponibilidade: dc.disponibilidade ?? '',
            rendas: dc.renda || dc.rendas || [],
            composicaoFamiliar: dc.composicaoFamiliar || [],
            enderecoComposicaoFamiliares: (() => {
                const currentFamiliares = [...(dc.enderecoComposicaoFamiliares || [])];
                if (addr.cep || addr.logradouro) {
                    const resIdx = currentFamiliares.findIndex(a => a.tipoEndereco === 'RESIDENCIAL');
                    const residencialAddr = {
                        tipoEndereco: 'RESIDENCIAL',
                        cep: cepComZeros(addr.cep),
                        logradouro: addr.logradouro || '',
                        numero: addr.numero || '',
                        complemento: addr.complemento || '',
                        bairro: addr.bairro || '',
                        cidade: getCidadeObject(addr.cidade),
                        uf: getCidadeObject(addr.cidade)?.uf?.uf || ''
                    };
                    if (resIdx > -1) {
                        residencialAddr.id = currentFamiliares[resIdx].id;
                        currentFamiliares[resIdx] = residencialAddr;
                    } else {
                        currentFamiliares.push(residencialAddr);
                    }
                }
                return currentFamiliares;
            })(),
            sistemaCotas: isEdit ? (dc.sistemaCotas ?? false) : false,
            itemSelecionadoPsInscricao: dc.psInscricao?.id || dc.itemSelecionadoPsInscricao || null,
            idProcessoSeletivo: identificacao.idProcessoSeletivo || null,
            categoriaSelecionadoPsInscricao: dc.categoria || dc.categoriaSelecionadoPsInscricao || '',
            inscricaoVeterano: isEdit ? (dc.inscricaoVeterano || estadoInicialInscricao.inscricaoVeterano) : estadoInicialInscricao.inscricaoVeterano,
            inscricaoEnsino: isEdit ? (dc.inscricaoEnsino || []) : [],
            /*
             * Em edição, `utilizarEnem` sempre reflete o `utilizacaoNota` gravado, mesmo que a
             * etapa "Dados da inscrição" nunca chegue a montar nesta sessão (edição de outra
             * etapa não pode trocar o ENEM por 'P' silenciosamente no payload).
             */
            inscricaoEnem: isEdit
                ? { ...(dc.inscricaoEnem || estadoInicialInscricao.inscricaoEnem), utilizarEnem: dc.utilizacaoNota === 'E' }
                : estadoInicialInscricao.inscricaoEnem,
            motivo: isEdit ? (dc.motivo || '') : '',
            utilizacaoNota: isEdit ? (dc.utilizacaoNota || '') : '',
            inscricaoAnexos: isEdit ? (dc.inscricaoAnexos || []) : [],
            aceitarTermos: false,
            permitirEntregaDocumentos: identificacao.permitirEntregaDocumentos ?? dc.permitirEntregaDocumentos ?? false,
            permitirEntregaDocumentoPendentes: identificacao.permitirEntregaDocumentoPendentes ?? dc.permitirEntregaDocumentoPendentes ?? false,
            inscricaoPendenciaAnexos: identificacao.inscricaoPendenciaAnexos ?? dc.inscricaoPendenciaAnexos ?? [],
            documentosExigidos: identificacao.documentosExigidos ?? dc.documentosExigidos ?? [],
            programaId: identificacao.programaId ?? null,
            configuracoesFormulario: identificacao.configuracoesFormulario ?? [],
            inscricoesProcessoSeletivo: identificacao.inscricoesProcessoSeletivo ?? [],
            categoriasInscricao: identificacao.categoriasInscricao ?? []
        };
    }

    const candidateDob = formatarDataDDMMYYYY(identificacao.dataNascimento);
    return {
        ...estadoInicialInscricao,
        itemSelecionadoPsInscricao: estadoInicialInscricao.itemSelecionadoPsInscricao,
        idProcessoSeletivo: identificacao.idProcessoSeletivo || estadoInicialInscricao.idProcessoSeletivo,
        pessoa: {
            ...estadoInicialInscricao.pessoa,
            cpf: identificacao.cpf || estadoInicialInscricao.pessoa.cpf,
            nome: identificacao.nome || estadoInicialInscricao.pessoa.nome,
            nomeMae: identificacao.nomeMae || estadoInicialInscricao.pessoa.nomeMae,
            dataNascimento: candidateDob || estadoInicialInscricao.pessoa.dataNascimento,
            emailAtual: estadoInicialInscricao.pessoa.emailAtual || ''
        },
        permitirEntregaDocumentos: identificacao.permitirEntregaDocumentos ?? false,
        permitirEntregaDocumentoPendentes: identificacao.permitirEntregaDocumentoPendentes ?? false,
        inscricaoPendenciaAnexos: identificacao.inscricaoPendenciaAnexos ?? [],
        documentosExigidos: identificacao.documentosExigidos ?? [],
        programaId: identificacao.programaId ?? null,
        configuracoesFormulario: identificacao.configuracoesFormulario ?? [],
        inscricoesProcessoSeletivo: identificacao.inscricoesProcessoSeletivo ?? [],
        categoriasInscricao: identificacao.categoriasInscricao ?? []
    };
};

const splitPhone = (phoneStr) => {
    if (!phoneStr) return { ddd: '', number: '' };
    const clean = String(phoneStr).replace(/\D/g, '');
    if (clean.length > 2) {
        return {
            ddd: clean.substring(0, 2),
            number: clean.substring(2)
        };
    }
    return { ddd: '', number: clean };
};

/*
 * Payload de /rs/inscricao/salvar e /rs/inscricao/atualizar a partir dos
 * valores acumulados do formulário. `identificacao` traz os ids já
 * conhecidos (pessoa, endereços, responsável) e `formulario` as opções
 * carregadas para a inscrição (programa, configuração dos campos etc).
 */
export const montarPayload = (valores, { identificacao, formulario } = {}) => {
    const telInfo = splitPhone(valores.pessoa?.telefone);
    const celInfo = splitPhone(valores.pessoa?.celular);
    const celInfoDos = splitPhone(valores.pessoa?.celularDois);

    /*
     * Emancipação: o formulário guarda `dataEmancipacao` na raiz do estado (e
     * não em `pessoa`), e só é enviada — junto com `emancipada='SIM'` — quando
     * o menor escolheu ser tratado como emancipado. `nomeResponsavel` só faz
     * sentido para menor com responsável (não emancipado).
     */
    const menor = calcularIdade(valores.pessoa?.dataNascimento) < 18;
    const tipo = valores.tipoResponsavelLegal;
    const emancipado = menor && tipo === 'EMANCIPADO';

    const mappedPessoa = {
        id: identificacao?.dadosCadastrais?.pessoa?.id || null,
        tipo: valores.pessoa?.tipo || 'FISICA',
        nome: valores.pessoa?.nome || '',
        tipoDocumento: valores.pessoa?.tipoDocumento || null,
        numeroDocumento: valores.pessoa?.numeroDocumento || null,
        digitoVerificador: valores.pessoa?.digitoVerificador || null,
        cpf: valores.pessoa?.cpf || '',
        sexo: valores.pessoa?.sexo || '',
        dataNascimento: valores.pessoa?.dataNascimento || null,
        nomeResponsavel: (menor && tipo && tipo !== 'EMANCIPADO') ? (valores.responsavelLegal?.nome || null) : null,
        nomePai: valores.pessoa?.nomePai || '',
        nomeMae: valores.pessoa?.nomeMae || '',
        rg: valores.pessoa?.rg || '',
        orgaoRg: valores.pessoa?.orgaoRg || '',
        pis: valores.pessoa?.pis || null,
        naturalidade: valores.pessoa?.naturalidade || null,
        nacionalidade: valores.pessoa?.nacionalidade ? { id: Number(valores.pessoa.nacionalidade.id || valores.pessoa.nacionalidade) } : null,
        estadoCivil: valores.pessoa?.estadoCivil ? { id: Number(valores.pessoa.estadoCivil.id || valores.pessoa.estadoCivil) } : null,
        raca: valores.pessoa?.raca ? { id: Number(valores.pessoa.raca.id || valores.pessoa.raca) } : null,
        passaport: valores.pessoa?.passaport || null,
        nomeSocial: valores.pessoa?.nomeSocial || '',
        emancipada: emancipado ? 'SIM' : 'NAO',
        dataEmancipacao: emancipado ? (valores.dataEmancipacao || null) : null,
        emailAtual: valores.pessoa?.emailAtual || '',
        enderecos: [
            {
                id: {
                    idPessoa: identificacao?.dadosCadastrais?.pessoa?.enderecos?.[0]?.id?.idPessoa || null,
                    tipoEndereco: 'RESIDENCIAL'
                },
                logradouro: valores.endereco?.logradouro || '',
                numero: valores.endereco?.numero || '',
                complemento: valores.endereco?.complemento || '',
                bairro: valores.endereco?.bairro || '',
                cep: valores.endereco?.cep ? Number(String(valores.endereco.cep).replace(/\D/g, '')) : null,
                cidade: valores.endereco?.cidade ? { id: Number(valores.endereco.cidade.id || valores.endereco.cidade) } : null,
                enviaCorrespondencia: 'SIM',
                dddTelefonePessoa: telInfo.ddd ? Number(telInfo.ddd) : null,
                telefonePessoa: telInfo.number || '',
                dddCelularPessoa: celInfo.ddd ? Number(celInfo.ddd) : null,
                celularPessoa: celInfo.number || '',
                dddCelularPessoaDois: celInfoDos.ddd ? Number(celInfoDos.ddd) : null,
                celularPessoaDois: celInfoDos.number || '',
                statusRegistro: identificacao?.dadosCadastrais?.pessoa?.enderecos?.[0]?.statusRegistro || 'ATIVO'
            }
        ]
    };

    if (menor && valores.responsavelLegal && tipo && tipo !== 'EMANCIPADO') {
        const respCelInfo = splitPhone(valores.responsavelLegal.celular);
        mappedPessoa.responsavelAtual = {
            id: identificacao?.dadosCadastrais?.pessoa?.responsavelAtual?.id || null,
            tipoResponsavel: valores.tipoResponsavelLegal,
            responsavel: {
                id: identificacao?.dadosCadastrais?.pessoa?.responsavelAtual?.responsavel?.id || null,
                tipo: 'FISICA',
                nome: valores.responsavelLegal.nome,
                cpf: valores.responsavelLegal.cpf,
                rg: valores.responsavelLegal.rg,
                dataNascimento: valores.responsavelLegal.dataNascimento || null,
                emailAtual: valores.responsavelLegal.emailAtual,
                enderecos: [
                    {
                        id: {
                            idPessoa: identificacao?.dadosCadastrais?.pessoa?.responsavelAtual?.responsavel?.enderecos?.[0]?.id?.idPessoa || null,
                            tipoEndereco: 'RESIDENCIAL'
                        },
                        logradouro: valores.responsavelLegal.endereco?.logradouro || '',
                        numero: valores.responsavelLegal.endereco?.numero || '',
                        complemento: valores.responsavelLegal.endereco?.complemento || '',
                        bairro: valores.responsavelLegal.endereco?.bairro || '',
                        cep: valores.responsavelLegal.endereco?.cep ? Number(String(valores.responsavelLegal.endereco.cep).replace(/\D/g, '')) : null,
                        cidade: valores.responsavelLegal.endereco?.cidade ? { id: Number(valores.responsavelLegal.endereco.cidade.id || valores.responsavelLegal.endereco.cidade) } : null,
                        enviaCorrespondencia: 'SIM',
                        dddCelularPessoa: respCelInfo.ddd ? Number(respCelInfo.ddd) : null,
                        celularPessoa: respCelInfo.number || '',
                        statusRegistro: identificacao?.dadosCadastrais?.pessoa?.responsavelAtual?.responsavel?.enderecos?.[0]?.statusRegistro || 'ATIVO'
                    }
                ]
            }
        };
    }

    const parentes = (valores.parentesBeneficiarios || []).map((pb) => {
        return {
            id: pb.id || null,
            nome: pb.nome || '',
            bolsa: pb.bolsa || '',
            grauParentesco: pb.grauParentesco ? { id: Number(pb.grauParentesco.id || pb.grauParentesco) } : null
        };
    });

    const cf = (valores.composicaoFamiliar || []).map((cfItem) => {
        // A lista é a situação completa das ocupações/rendas do integrante;
        // ocupacao/rendaBruta seguem consolidadas para DSOCUPACAO/RENDABRUTA.
        const rendasMembro = rendasDoMembro(cfItem).map(renda => ({
            tpRenda: renda.tpRenda || 'P',
            ocupacao: String(renda.ocupacao || '').trim(),
            vlrRenda: parseDecimal(renda.vlrRenda) ?? 0
        }));
        return {
            id: cfItem.id || null,
            descricao: cfItem.descricao || '',
            ocupacao: consolidarOcupacoes(rendasMembro),
            rendaBruta: somarRendas(rendasMembro),
            rendas: rendasMembro,
            cpf: cfItem.cpf || null,
            rg: cfItem.rg || null,
            declaracaoIR: cfItem.declaracaoIR || 'N',
            dataNascimento: cfItem.dataNascimento || null,
            grauParentesco: cfItem.grauParentesco ? { id: Number(cfItem.grauParentesco.id || cfItem.grauParentesco) } : null,
            enderecoComposicaoFamiliar: cfItem.enderecoComposicaoFamiliar ? {
                id: cfItem.enderecoComposicaoFamiliar.id || null,
                tipoEndereco: cfItem.enderecoComposicaoFamiliar.tipoEndereco,
                logradouro: cfItem.enderecoComposicaoFamiliar.logradouro,
                numero: cfItem.enderecoComposicaoFamiliar.numero,
                complemento: cfItem.enderecoComposicaoFamiliar.complemento,
                bairro: cfItem.enderecoComposicaoFamiliar.bairro,
                cep: cfItem.enderecoComposicaoFamiliar.cep ? Number(String(cfItem.enderecoComposicaoFamiliar.cep).replace(/\D/g, '')) : null,
                cidade: cfItem.enderecoComposicaoFamiliar.cidade ? { id: Number(cfItem.enderecoComposicaoFamiliar.cidade.id || cfItem.enderecoComposicaoFamiliar.cidade) } : null
            } : null
        };
    });

    const rendasMap = (valores.rendas || valores.renda || []).map((r) => {
        return {
            id: r.id || null,
            tpRenda: r.tpRenda || '',
            ocupacao: r.ocupacao || '',
            vlrRenda: parseDecimal(r.vlrRenda) ?? 0
        };
    });

    const ensinos = (valores.inscricaoEnsino || []).map((e) => {
        return {
            id: e.id || null,
            dsCurso: e.dsCurso || '',
            instituicao: e.instituicao || '',
            completo: e.completo || null,
            ensinoPublico: e.ensinoPublico || null,
            dataConclusao: e.dataConclusao || null,
            formacao: e.formacao ? { id: Number(e.formacao.id || e.formacao) } : null
        };
    });

    const enderecosCF = (valores.enderecoComposicaoFamiliares || []).map((addr) => {
        return {
            id: addr.id || null,
            tipoEndereco: addr.tipoEndereco,
            logradouro: addr.logradouro,
            numero: addr.numero,
            complemento: addr.complemento,
            bairro: addr.bairro,
            cep: addr.cep ? Number(String(addr.cep).replace(/\D/g, '')) : null,
            cidade: addr.cidade ? { id: Number(addr.cidade.id || addr.cidade) } : null
        };
    });

    let veterano = null;
    if (formulario?.programaId === PROGRAMA_VETERANO && valores.inscricaoVeterano) {
        veterano = {
            possuiEnem: valores.inscricaoVeterano.possuiEnem ?? null,
            possuiBeneficioNaIes: valores.inscricaoVeterano.possuiBeneficioNaIes ?? null,
            descricaoBeneficio: valores.inscricaoVeterano.descricaoBeneficio || '',
            participouProcessoAnterior: valores.inscricaoVeterano.participouProcessoAnterior ?? null,
            descricaoProcessoAnterior: valores.inscricaoVeterano.descricaoProcessoAnterior || '',
            divida: valores.inscricaoVeterano.divida ? String(valores.inscricaoVeterano.divida) : 'N'
        };
    }

    /*
     * `utilizarEnem` só existe de fato quando a etapa "Dados da inscrição" já
     * inicializou o formulário; fora dela (ex.: editando só Dados Pessoais)
     * `utilizacaoNota`, gravado junto com o restante da inscrição, é quem sabe.
     */
    const usaEnem = typeof valores.inscricaoEnem?.utilizarEnem === 'boolean' ? valores.inscricaoEnem.utilizarEnem : valores.utilizacaoNota === 'E';

    let enem = null;
    if (usaEnem && valores.inscricaoEnem) {
        enem = {
            id: valores.inscricaoEnem.id || null,
            cdEnem: valores.inscricaoEnem.cdEnem ? Number(String(valores.inscricaoEnem.cdEnem).replace(/\D/g, '')) : null,
            pontuacao: parseDecimal(valores.inscricaoEnem.pontuacao),
            nomeArquivo: valores.inscricaoEnem.nomeArquivo || null,
            mimetype: valores.inscricaoEnem.mimetype || null,
            processoSeletivoEnem: valores.inscricaoEnem.processoSeletivoEnem ? { id: Number(valores.inscricaoEnem.processoSeletivoEnem.id || valores.inscricaoEnem.processoSeletivoEnem) } : null,
            arquivo: (typeof valores.inscricaoEnem.arquivo === 'string' && valores.inscricaoEnem.arquivo) ? valores.inscricaoEnem.arquivo : null
        };
    }

    const anexos = (valores.inscricaoAnexos || []).map(ia => {
        const base = {
            id: ia.id || null,
            idInscricaoPendenciaAnexo: ia.idInscricaoPendenciaAnexo || null,
            descricao: ia.descricao || '',
            documento: ia.documento ? { id: Number(ia.documento.id || ia.documento) } : null
        };

        // Anexo já salvo é imutável no back: não reenvia o arquivo.
        if (ia.id) {
            return {
                ...base,
                composicaoFamiliar: ia.composicaoFamiliar ? { id: ia.composicaoFamiliar.id || null } : null,
                anexo: ia.anexo ? {
                    id: ia.anexo.id || null,
                    nome: ia.anexo.nome,
                    mimetype: ia.anexo.mimetype,
                    tamanho: ia.anexo.tamanho,
                    descricao: ia.anexo.descricao || ''
                } : null
            };
        }

        // Anexo novo: candidato -> composicaoFamiliar null; membro com id -> {id}; membro novo -> índice na lista enviada.
        let composicaoFamiliar = null;
        let indiceComposicaoFamiliar;
        if (ia.composicaoFamiliar) {
            if (ia.composicaoFamiliar.id) {
                composicaoFamiliar = { id: ia.composicaoFamiliar.id };
            } else {
                composicaoFamiliar = { id: null };
                // Não achou o índice: manda sem ele, e o back rejeita em vez de anexar ao candidato por engano.
                const indice = encontrarIndiceMembro(valores.composicaoFamiliar || [], ia.composicaoFamiliar);
                if (indice >= 0) {
                    indiceComposicaoFamiliar = indice;
                }
            }
        }

        return {
            ...base,
            composicaoFamiliar,
            ...(indiceComposicaoFamiliar !== undefined ? { indiceComposicaoFamiliar } : {}),
            anexo: ia.anexo ? {
                id: ia.anexo.id || null,
                nome: ia.anexo.nome,
                mimetype: ia.anexo.mimetype,
                tamanho: ia.anexo.tamanho,
                descricao: ia.anexo.descricao || '',
                arquivo: ia.anexo.arquivo || null
            } : null
        };
    });

    return {
        id: valores.id || null,
        codigoAluno: identificacao?.dadosCadastrais?.codigoAluno ?? valores.codigoAluno ?? null,
        vaga: valores.vaga ? { id: Number(valores.vaga.id || valores.vaga) } : null,
        tipoMoradia: valores.tipoMoradia ? { id: Number(valores.tipoMoradia.id || valores.tipoMoradia) } : null,
        tipoHabitacao: valores.tipoHabitacao ? { id: Number(valores.tipoHabitacao.id || valores.tipoHabitacao) } : null,
        pessoa: mappedPessoa,
        psInscricao: valores.itemSelecionadoPsInscricao
            ? { id: Number(valores.itemSelecionadoPsInscricao?.id || valores.itemSelecionadoPsInscricao) }
            : null,
        sistemaCotas: valores.sistemaCotas ?? null,
        utilizacaoNota: usaEnem ? 'E' : (valores.utilizacaoNota === 'H' ? 'H' : 'P'),
        motivo: valores.motivo || '',
        disponibilidade: parseDecimal(valores.disponibilidade),
        vlrPensao: parseDecimal(valores.vlrPensao),
        vlrPensaoFilho: parseDecimal(valores.vlrPensaoFilho),
        declaracaoIR: valores.declaracaoIR || 'N',
        cadUnicoOuTransferenciaRenda: valores.cadUnicoOuTransferenciaRenda ?? null,
        categoria: valores.categoriaSelecionadoPsInscricao || valores.categoria || '',
        renda: rendasMap,
        composicaoFamiliar: cf,
        parentesBeneficiarios: parentes,
        inscricaoEnsino: ensinos,
        enderecoComposicaoFamiliares: enderecosCF,
        inscricaoVeterano: veterano,
        inscricaoEnem: enem,
        inscricaoAnexos: anexos
    };
};
